import { create } from 'zustand'
import type { SessionDTO, SessionLifetimeDTO, UserDTO } from '@shared/types'
import { api, UNAUTHORIZED_EVENT } from '@/lib/api'

/** What login, register and bootstrap answer with. */
interface SignedInResponse {
  user: UserDTO
  lifetime: SessionLifetimeDTO
}

interface AuthState {
  session: SessionDTO | null
  user: UserDTO | null
  /**
   * When this session dies, taking every connected site, image and generated alt
   * text with it. Null when unauthenticated. Kept here — not only on `session` —
   * because the warning components subscribe to it on its own.
   */
  lifetime: SessionLifetimeDTO | null
  loading: boolean

  loadSession: () => Promise<void>
  login: (email: string, password: string) => Promise<UserDTO>
  register: (email: string, name: string, password: string) => Promise<UserDTO>
  bootstrap: (email: string, name: string, password: string) => Promise<UserDTO>
  /** Slides the idle window forward. Null when unauthenticated, or when the beat failed. */
  heartbeat: () => Promise<SessionLifetimeDTO | null>
  /** Ends the session, so the server deletes its sites. The caller confirms first. */
  logout: () => Promise<void>
  changePassword: (current: string, next: string) => Promise<void>
}

/**
 * Folds a fresh user into the cached session. `allowRegistration` is an
 * instance-wide flag the login response does not repeat, so it is carried over
 * rather than guessed.
 */
function sessionWithUser(
  session: SessionDTO | null,
  user: UserDTO | null,
  lifetime: SessionLifetimeDTO | null
): SessionDTO {
  return {
    user,
    needsBootstrap: false,
    allowRegistration: session?.allowRegistration ?? false,
    lifetime
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  user: null,
  lifetime: null,
  // Starts true so the router waits for the session probe instead of bouncing a
  // signed-in user to /login on first paint.
  loading: true,

  loadSession: async () => {
    set({ loading: true })
    try {
      const session = await api.get<SessionDTO>('/auth/session')
      set({ session, user: session.user, lifetime: session.lifetime, loading: false })
    } catch {
      // /auth/session is public, so a failure here is the network or the server
      // being down. Land on the login screen rather than an infinite spinner.
      set({ session: null, user: null, lifetime: null, loading: false })
    }
  },

  login: async (email, password) => {
    // Sign-in answers with the lifetime, so the countdown starts without a
    // follow-up round trip.
    const { user, lifetime } = await api.post<SignedInResponse>('/auth/login', { email, password })
    set((state) => ({
      user,
      lifetime,
      session: sessionWithUser(state.session, user, lifetime),
      loading: false
    }))
    return user
  },

  register: async (email, name, password) => {
    const { user, lifetime } = await api.post<SignedInResponse>('/auth/register', {
      email,
      name,
      password
    })
    set((state) => ({
      user,
      lifetime,
      session: sessionWithUser(state.session, user, lifetime),
      loading: false
    }))
    return user
  },

  bootstrap: async (email, name, password) => {
    const { user, lifetime } = await api.post<SignedInResponse>('/auth/bootstrap', {
      email,
      name,
      password
    })
    set((state) => ({
      user,
      lifetime,
      session: sessionWithUser(state.session, user, lifetime),
      loading: false
    }))
    return user
  },

  heartbeat: async () => {
    if (!get().user) return null
    try {
      const lifetime = await api.post<SessionLifetimeDTO>('/auth/heartbeat')
      // Signing out while the request was in flight must not resurrect a lifetime.
      const user = get().user
      if (!user) return null
      set((state) => ({ lifetime, session: sessionWithUser(state.session, user, lifetime) }))
      return lifetime
    } catch {
      // A 401 has already signed the user out through UNAUTHORIZED_EVENT, and a
      // transient failure is covered by the next tick. Neither is actionable here.
      return null
    }
  },

  logout: async () => {
    try {
      await api.post<void>('/auth/logout')
    } finally {
      // Even a failed logout must sign the user out locally.
      set((state) => ({
        user: null,
        lifetime: null,
        session: sessionWithUser(state.session, null, null),
        loading: false
      }))
    }
  },

  changePassword: async (current, next) => {
    await api.post<void>('/auth/change-password', { currentPassword: current, newPassword: next })
    const { user } = get()
    if (user?.mustChangePassword) {
      set({ user: { ...user, mustChangePassword: false } })
    }
  }
}))

// A 401 on any request means the cookie is gone or expired. Clearing the user
// here is what flips <RequireAuth> over to the login route.
window.addEventListener(UNAUTHORIZED_EVENT, () => {
  const { user, session } = useAuthStore.getState()
  // A failed sign-in attempt is also a 401; with no user there is nothing to clear.
  if (!user) return
  useAuthStore.setState({
    user: null,
    lifetime: null,
    session: sessionWithUser(session, null, null),
    loading: false
  })
})
